import React, { useState, useEffect, useRef } from "react";
import axios from "axios";
import { GoogleMap, LoadScript, Marker, InfoWindow } from '@react-google-maps/api';
import { FaGlobeAmericas, FaChevronUp, FaChevronDown, FaPaperPlane, FaChevronLeft, FaTimes, FaArrowUp, FaArrowDown, FaSpinner } from 'react-icons/fa';
import AssistantMessage from './AssistantMessage';
import { useMediaQuery } from 'react-responsive';

const World = ({ apiKey, chat, formToken, initialMapOptions = {} }) => {
  const chatId = chat?.id;
  const isMobile = useMediaQuery({ maxWidth: 768 });
  const [map, setMap] = useState(null);
  const [center, setCenter] = useState({ lat: 20, lng: 0 });
  const [clickedLocation, setClickedLocation] = useState(null);
  const [infoWindowOpen, setInfoWindowOpen] = useState(false);
  const [question, setQuestion] = useState('');
  const [messages, setMessages] = useState(chat?.messages || []);
  const [isLoading, setIsLoading] = useState(false);
  const [suggestions, setSuggestions] = useState([]);
  const [isChatExpanded, setIsChatExpanded] = useState(!isMobile);
  const [isChatMinimized, setIsChatMinimized] = useState(false);
  const [scrollPosition, setScrollPosition] = useState(0);
  const [authToken, setAuthToken] = useState('');
  const [isMapsLoaded, setIsMapsLoaded] = useState(false);

  // Initialize messages from chat prop when it changes
  useEffect(() => {
    if (chat?.messages?.length > 0) {
      setMessages(chat.messages);
      // Check if there's a map focus in the last assistant message
      const lastMessage = chat.messages[chat.messages.length - 1];
      if (lastMessage?.map_focus) {
        const { latitude, longitude, zoom = 6 } = lastMessage.map_focus;
        const newCenter = {
          lat: parseFloat(latitude),
          lng: parseFloat(longitude)
        };
        setCenter(newCenter);
        setClickedLocation(newCenter);
      }
    }
  }, [chat]);

  // Get the CSRF token from the meta tag and check if Google Maps is loaded
  useEffect(() => {
    const token = document.querySelector('meta[name="csrf-token"]')?.content;
    if (token) {
      setAuthToken(token);
    }

    // Check if Google Maps is already loaded
    if (window.google?.maps) {
      setIsMapsLoaded(true);
    } else {
      // Set up a listener for when Google Maps loads
      const checkMapsLoaded = setInterval(() => {
        if (window.google?.maps) {
          setIsMapsLoaded(true);
          clearInterval(checkMapsLoaded);
        }
      }, 100);

      return () => clearInterval(checkMapsLoaded);
    }
  }, []);
  const messagesEndRef = useRef(null);
  const messagesContainerRef = useRef(null);
  const formRef = useRef(null);

  const defaultSuggestions = [
    "Tell me about the history of this place",
    "What's the local culture like?",
    "What are some interesting landmarks here?"
  ];

  const scrollToBottom = () => {
    if (messagesContainerRef.current) {
      messagesContainerRef.current.scrollTop = messagesContainerRef.current.scrollHeight;
      setScrollPosition(messagesContainerRef.current.scrollTop);
    }
  };

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  const handleScroll = (direction) => {
    if (messagesContainerRef.current) {
      const container = messagesContainerRef.current;
      const scrollAmount = direction === 'up' ? -100 : 100;
      const newPosition = container.scrollTop + scrollAmount;
      container.scrollTop = newPosition;
      setScrollPosition(newPosition);
    }
  };

  const containerStyle = {
    width: '100%',
    height: '100%',
    position: 'absolute',
    top: 0,
    left: 0
  };

  // Initialize map options after Google Maps is loaded
  const getMapOptions = () => {
    // Use default values that don't require google.maps
    return {
      ...initialMapOptions,
      mapTypeControl: true,
      mapTypeControlOptions: {
        style: 0, // HORIZONTAL_BAR
        position: 5, // TOP_LEFT
        mapTypeIds: ['roadmap', 'terrain', 'satellite', 'hybrid'],
        ...(initialMapOptions.mapTypeControlOptions || {})
      },
      zoomControl: true,
      zoomControlOptions: {
        position: 5, // LEFT_CENTER
        ...(initialMapOptions.zoomControlOptions || {})
      },
      fullscreenControl: true,
      fullscreenControlOptions: {
        position: 1, // LEFT_TOP
        ...(initialMapOptions.fullscreenControlOptions || {})
      },
      streetViewControl: false,
      styles: [
        {
          featureType: "poi",
          elementType: "labels",
          stylers: [{ visibility: "off" }]
        },
        ...(initialMapOptions.styles || [])
      ]
    };
  };

  const handleMapClick = (event) => {
    const newLocation = {
      lat: event.latLng.lat(),
      lng: event.latLng.lng()
    };
    setClickedLocation(newLocation);
    setSuggestions(defaultSuggestions);
  };

  const handleQuestionSubmit = async (e, questionText = null) => {
    // Only prevent default if it's a form submission event
    if (e && e.preventDefault) e.preventDefault();
    const textToSend = questionText || question.trim();
    if (!textToSend) return;

    setIsLoading(true);
    setQuestion('');

    try {
      const response = await axios.post(`/api/v1/chats/${chatId}/send_message`, {
        content: textToSend,
        // Only include coordinates if a location was clicked
        ...(clickedLocation && {
          latitude: clickedLocation.lat,
          longitude: clickedLocation.lng
        })
      }, {
        headers: {
          'X-CSRF-Token': formToken,
          'Content-Type': 'application/json',
          'Accept': 'application/json'
        }
      });

      const message = response.data;

      // Update map if location focus is provided
      console.log('message', message);
      if (message.map_focus) {
        const { latitude, longitude, zoom = 6 } = message.map_focus;
        const newCenter = {
          lat: parseFloat(latitude),
          lng: parseFloat(longitude)
        };

        console.log('Panning to:', { ...newCenter, zoom });

        // Update center state and marker
        setCenter(newCenter);
        setClickedLocation(newCenter);
        
        // Ensure the map exists and update its view
        if (map) {
          map.panTo(newCenter);
          if (zoom) {
            map.setZoom(parseInt(zoom));
          }
        }

        if (map) {
          // Force the map to update its center
          map.panTo(newCenter);
          if (zoom) {
            map.setZoom(parseInt(zoom));
          }
        }
      }

      setMessages(prev => [...prev, message]);
    } catch (error) {
      console.error('Error:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleSuggestionClick = (suggestion) => {
    setQuestion(suggestion);
    // Focus the input after setting the question
    setTimeout(() => {
      formRef.current?.querySelector('textarea')?.focus();
    }, 0);
  };

  // Handle touch events to prevent map interaction when scrolling chat
  useEffect(() => {
    if (isMobile) {
      document.body.style.overflow = 'hidden';
      return () => {
        document.body.style.overflow = 'auto';
      };
    }
  }, [isMobile]);

  return (
    <div className="relative h-screen w-full flex flex-col md:flex-row overflow-hidden">
      {/* Map Section - Full screen on mobile, flex-1 on desktop */}
      <div
        className={`${isMobile ? 'fixed inset-0' : 'flex-1 w-full'} relative`}
        style={isMobile ? { touchAction: 'pan-y' } : {}}>
        {isMobile && (
          <div className="absolute top-0 left-0 right-0 z-10 p-2 bg-gradient-to-b from-black/20 to-transparent">
            <button
              onClick={() => window.history.back()}
              className="p-2 bg-white/90 rounded-lg shadow-md"
            >
              <FaChevronLeft className="text-gray-700" />
            </button>
          </div>
        )}
        <LoadScript 
          googleMapsApiKey={apiKey}
          onLoad={() => setIsMapsLoaded(true)}
        >
          {isMapsLoaded ? (
            <GoogleMap
              mapContainerStyle={{
                width: '100%',
                height: '100vh',
                margin: 0,
                padding: 0
              }}
              center={center}
              zoom={3}
              options={getMapOptions()}
              onClick={handleMapClick}
              onLoad={(map) => {
                setMap(map);
                map.panTo(center);
                map.setZoom(3);
                setTimeout(() => {
                  window.dispatchEvent(new Event('resize'));
                }, 100);
              }}
            >
              {clickedLocation && isMapsLoaded && window.google?.maps && (
                <Marker
                  position={clickedLocation}
                  icon={{
                    url: 'https://maps.google.com/mapfiles/ms/icons/red-dot.png',
                    scaledSize: new window.google.maps.Size(32, 32)
                  }}
                />
              )}
            </GoogleMap>
          ) : (
            <div className="flex items-center justify-center h-full bg-gray-100">
              <div className="text-center">
                <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-blue-500 mx-auto"></div>
                <p className="mt-2 text-gray-600">Loading map...</p>
              </div>
            </div>
          )}
        </LoadScript>
      </div>

      {/* Simple Chat Panel */}
      <div className={`w-full md:w-96 border-l flex flex-col bg-white transition-all duration-300 ease-in-out ${isChatMinimized ? 'h-16' : 'h-1/2'}`} style={{
        height: isMobile ? (isChatMinimized ? '64px' : '50vh') : '100%',
        maxHeight: isMobile ? '100%' : 'none',
        position: isMobile ? 'fixed' : 'relative',
        bottom: 0,
        left: 0,
        right: 0,
        zIndex: 1000,
        overflow: 'hidden'
      }}>
        {/* Simple Header with Toggle */}
        <div className="p-3 bg-blue-500 text-white flex justify-between items-center">
          <div>
            <h2 className="text-lg font-bold">Ask Me Anything!</h2>
            {!isChatMinimized && <p className="text-sm opacity-90">Type your question about any place in the world</p>}
          </div>
          {isMobile && (
            <button 
              onClick={() => setIsChatMinimized(!isChatMinimized)}
              className="p-2 rounded-full hover:bg-blue-600 transition-colors"
              aria-label={isChatMinimized ? 'Expand chat' : 'Minimize chat'}
            >
              {isChatMinimized ? (
                <FaChevronUp className="text-white" />
              ) : (
                <FaChevronDown className="text-white" />
              )}
            </button>
          )}
        </div>

        {/* Simple Messages Area */}
        <div
          ref={messagesContainerRef}
          className={`flex-1 p-4 overflow-y-auto transition-all duration-300 ease-in-out ${isChatMinimized ? 'opacity-0 h-0' : 'opacity-100'}`}
          style={{
            minHeight: isChatMinimized ? 0 : '100px',
            background: '#f9fafb',
            borderBottom: '1px solid #e5e7eb',
            overflow: isChatMinimized ? 'hidden' : 'auto'
          }}
        >
          {messages.length === 0 ? (
            <div className="text-center text-gray-500 mt-4">
              <p className="text-lg font-medium mb-2">👋 Hello Explorer!</p>
              <p className="text-sm">Ask me anything about any place in the world!</p>
              <p className="text-xs mt-2 text-gray-400">Example: "Tell me about the Eiffel Tower"</p>
            </div>
          ) : (
            messages.map((msg, index) => (
              <div key={index} className="mb-4">
                {msg.role === 'assistant' ? (
                  <div className="bg-white p-3 rounded-lg shadow-sm border">
                    <AssistantMessage 
                      message={msg} 
                      onFollowUpClick={(question) => handleQuestionSubmit(null, question)}
                      formToken={formToken}
                    />
                  </div>
                ) : (
                  <div className="flex justify-end">
                    <div className="bg-blue-500 text-white rounded-lg px-4 py-2 max-w-[80%]">
                      {msg.content}
                    </div>
                  </div>
                )}
              </div>
            ))
          )}
          <div ref={messagesEndRef} />
        </div>

        {/* Simple Input Section */}
        <div className={`p-4 bg-white border-t transition-all duration-300 ease-in-out ${isChatMinimized ? 'opacity-0 h-0 p-0 overflow-hidden' : 'opacity-100'}`}>
          <form ref={formRef} onSubmit={handleQuestionSubmit}>
            <div className="relative">
              <input
                type="text"
                value={question}
                onChange={(e) => setQuestion(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') {
                    e.preventDefault();
                    handleQuestionSubmit(e);
                  }
                }}
                placeholder="Type your question here..."
                className="w-full p-3 pr-12 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 disabled:opacity-70"
                autoFocus
                disabled={isLoading}
              />
              <button
                type="submit"
                disabled={isLoading || !question.trim()}
                className="absolute right-2 top-1/2 transform -translate-y-1/2 p-2 text-blue-500 hover:text-blue-700 disabled:opacity-30"
              >
                {isLoading ? (
                  <FaSpinner className="w-5 h-5 animate-spin" />
                ) : (
                  <FaPaperPlane className="w-5 h-5" />
                )}
              </button>
            </div>
            <div className="mt-2 text-xs text-gray-500 text-center">
              Try: "Tell me about the Eiffel Tower" or "Show me the Great Barrier Reef"
            </div>
          </form>
          {isMobile && (
            <div className="absolute bottom-0 left-0 right-0 h-8 bg-gradient-to-t from-white to-transparent" />
          )}
        </div>
      </div>
    </div>
  );
};

export default World;
